import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { ParkingClient } from '../../../../../core/models/parking/parking-client.model';
import { MapperParkingClientService } from '../../../../../core/mappers/parking/mapper-parking-client.service';
import { ApiParkingClientService } from '../../../../../core/services/api/parking/api-parking-client.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalParkingClientComponent } from '../../../modals/parking/modal-parking-client/modal-parking-client.component';

@Component({
  selector: 'app-parking-client',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalParkingClientComponent],
  templateUrl: './parking-client.component.html',
  styleUrl: './parking-client.component.css',
})
export class ParkingClientComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiParkingClientService);
  private readonly mapper = inject(MapperParkingClientService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  items = signal<ParkingClient[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  selectedId = signal(0);
  searchText = signal('');
  filterClientRole: '' | 'distributor' | 'tenant' | 'standalone' = '';
  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions = [10, 20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(0);

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  readonly roleOptions: { value: '' | 'distributor' | 'tenant' | 'standalone'; label: string }[] = [
    { value: '', label: 'ทุกบทบาท' },
    { value: 'distributor', label: 'ฝ่ายอาคาร' },
    { value: 'tenant', label: 'ผู้เช่า' },
    { value: 'standalone', label: 'อิสระ' },
  ];

  constructor() {
    effect(() => {
      const siteId = this.siteState.selectedSite()?.id;
      untracked(() => {
        this.currentPage.set(1);
        if (siteId) this.loadTable();
        else this.items.set([]);
      });
    });
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onSearchChange(): void {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => {
      this.currentPage.set(1);
      this.loadTable();
    }, 350);
  }

  onRoleFilterChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจากแถบด้านบน');
      return;
    }
    this.selectedId.set(0);
    this.isModalOpen.set(true);
  }

  openEdit(item: ParkingClient): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedId.set(item.id);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedId.set(0);
  }

  onSaveSuccess(): void {
    this.loadTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadTable();
  }

  roleLabel(role: string | null | undefined): string {
    return this.mapper.roleLabel(role);
  }

  trackById(_index: number, item: ParkingClient): number {
    return item.id;
  }

  private loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.items.set([]);
      return;
    }

    this.isLoading.set(true);
    this.api.getForTable({
      siteId,
      page: this.currentPage(),
      pageSize: this.pageSize(),
      searchText: this.searchText().trim() || undefined,
      clientRole: this.filterClientRole || undefined,
    }).subscribe({
      next: (res) => {
        const raw = res as unknown as Record<string, unknown>;
        const list = (raw['data'] ?? raw['Data'] ?? []) as unknown[];
        this.items.set(list.map((row) => this.mapper.mapFromDto(row as never)));
        this.totalItems.set(Number(raw['total'] ?? raw['Total'] ?? 0));
        this.totalPages.set(Number(raw['totalPages'] ?? raw['TotalPages'] ?? 0));
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดข้อมูลบริษัทผู้เช่าได้');
        this.isLoading.set(false);
      },
    });
  }
}
